import React, { useCallback, useEffect, useMemo, useState } from "react";
import Auth from "./sdk/Auth";
import SummaryView from "./SummaryView";
import TrendView from "./TrendView";
import "./report.css";

const CaseReport = () => {
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [refreshTime, setRefreshTime] = useState("");
    const [selectedOrg, setSelectedOrg] = useState("All");
    const [selectedUsage, setSelectedUsage] = useState("production");
    const [activeTab, setActiveTab] = useState("trend");
    const [timeZone, setTimeZone] = useState(Auth.getEnv().timeZone);

    const [startDate, setStartDate] = useState(() => {
        const date = new Date();
        date.setDate(date.getDate() - 30);
        return date.toISOString().slice(0, 10);
    });
    const [endDate, setEndDate] = useState(() => {
        const date = new Date();
        return date.toISOString().slice(0, 10);
    });

    const udsDomain = Auth.getEnv().uds_domain || "";

    const regionName = useMemo(() => {

        if (udsDomain.includes("dev")) return "Dev";
        if (udsDomain.includes("qa")) return "QA";
        if (udsDomain.includes("memj")) return "Japan";

        return "Production";

    }, [udsDomain]);

const filteredList = useMemo(() => {
        return list.filter(item => {
            const orgMatch =
                selectedOrg === "All"
                || String(item.orgId) === selectedOrg;
            const usageMatch =
                selectedUsage === "All"
                || item.usage === selectedUsage;
            return orgMatch && usageMatch;
        });
    }, [list, selectedOrg, selectedUsage]);

    const patientCount = useMemo(() => {

        return new Set(
            filteredList
                .map(item => item.patientId)
                .filter(Boolean)
        ).size;

    }, [filteredList]);

    const orgList = useMemo(() => {

        const set = new Set();

        list.forEach(item => {

            if (
                selectedUsage !== "All"
                && item.usage !== selectedUsage
            ) {
                return;
            }

            set.add(String(item.orgId));

        });

        return Array.from(set).sort();

    }, [list, selectedUsage]);

    const usageList = useMemo(() => {

        const set = new Set();

        list.forEach(item => {

            if (item.usage) {
                set.add(item.usage);
            }

        });

        return Array.from(set).sort();

    }, [list]);    

    const loadData = useCallback(async (dateValue = startDate, timezone=timeZone) => {

        setLoading(true);
        setError("");

        let retry = 1;
        while (retry-- >= 0) {
            try {
                console.log(Auth.getLoginInfo())
                const api =
                    `${Auth.getEnv().uds_domain}/report/api/case?createTime=${dateValue}&timezone=${timezone}`;
                const response = await fetch(api, {
                    method: "GET",
                    credentials: "include"
                });
                if (response.ok) {
                    const result = await response.json();
                    if (result.code === 0 && result.data) {
                        let offset = parseInt(timezone);
                        result.data.forEach(item => {
                            let time = new Date(item.createTime)
                            time = time.setHours(time.getHours() + offset); 
                            item.createTime = time 
                        })
                        setList(result.data);
                        setRefreshTime(new Date().toLocaleString());
                        break;
                    } else if (result.msg) {
                        throw new Error(result.msg);
                    } else {
                        throw new Error("Failed to load data");
                    }
                } else if (response.status === 403) {
                    throw new Error("No permission, you are not Report Viewer");
                } else if (response.status === 401) {
                    throw new Error("No permission, Invalid token");
                }
            } catch (e) {
                setList([]);
                let errMsg = e;
                const now = Date.now() / 1000
                console.log(Auth.getLoginInfo())
                const expireTime = Auth.getLoginInfo().accessToken.expireTimestamp
                if (now > expireTime) {
                    const refreshResult = await Auth.refreshToken();
                    if (!refreshResult.ok) {      
                        errMsg = "Login Expired, please relogin";                        
                        console.error(errMsg);
                        break;
                    };
                } else {
                    setError(errMsg);
                    break;
                }
            } finally {
                setLoading(false);
            }
        }
    }, [startDate, timeZone]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const exportCSV = () => {
        if (filteredList.length === 0) return;
        const headers = [
            "orgId",
            "caseId",
            "caseNo",
            "patientId",
            "patientNo",
            "userId",
            "user",
            "createTime"
        ];
        const rows = filteredList.map(item => [
            item.orgId,
            item.caseId,
            item.caseNo || "",
            item.patientId,
            item.patientNo || "",
            item.userId || "",
            item.user || "",
            formatDate(item.createTime)
        ]);

        const csv = [
            headers.join(","),
            ...rows.map(r =>
                r.map(v => `"${v}"`).join(",")
            )
        ].join("\n");
        const blob = new Blob(
            [csv],
            {
                type: "text/csv;charset=utf-8;"
            }
        );
        const url =
            URL.createObjectURL(blob);
        const link =
            document.createElement("a");
        link.href = url;
        link.download =
            `case_report_${startDate}_to_${endDate}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    };
    return (
        <div className="page modern-page">
            <div className="sticky-header">
                <h1>
                    {regionName} Case Statistics
                </h1>
                <div className="toolbar">
                    <button
                        className="primary-btn"
                        onClick={exportCSV}
                    >
                        Export CSV
                    </button>
                    <span
                        className="logout-link"
                        onClick={() =>
                            Auth.logoutWithCallback()
                        }
                    >
                        ⎋ Logout
                    </span>
                </div>
            </div>
            <div className="sticky-summary">
                <div className="card">
                    <div className="card-title">
                        Cases
                    </div>
                    <div className="card-value">
                        {filteredList.length}
                    </div>
                </div>
                <div className="card">
                    <div className="card-title">
                        Patients
                    </div>
                    <div className="card-value patient-value">
                        {patientCount}
                    </div>
                </div>
                <div className="card">
                    <div className="card-title">
                        Start Date
                    </div>
                    <input
                        type="date"
                        min="2026-05-15"
                        value={startDate}
                        onChange={e => {
                            const value = e.target.value;
                            setStartDate(value);
                            loadData(value);
                        }}
                        className="inline-date-tight"
                    />

                </div>
                <div className="card">
                    <div className="card-title">
                        Time Zone
                    </div>
                    <select
                        value={timeZone}
                        onChange={e => setTimeZone(e.target.value)}
                        className="inline-select"
                    >
                        <option value="0">
                            UTC
                        </option>
                        <option value="9">
                            JST(Asia/Tokyo)
                        </option>
                        <option value="8">
                            CST(Asia/Shanghai)
                        </option>
                        <option value="-7">
                            PDT/PST(America/LosAngeles)
                        </option>
                        <option value="-4">
                            EDT/EST(America/NewYork)
                        </option>
                    </select>
                </div>
                <div className="card">
                    <div className="card-title org-title">
                        Usage
                    </div>
                    <select
                        value={selectedUsage}
                        onChange={e => {
                            setSelectedUsage(e.target.value);
                            setSelectedOrg("All");
                        }}
                        className="inline-select"
                    >
                        <option value="All">
                            All
                        </option>
                        {
                            usageList.map(usage => (
                                <option
                                    key={usage}
                                    value={usage}
                                >
                                    {usage}
                                </option>
                            ))
                        }
                    </select>
                </div>
                <div className="card">
                    <div className="card-title org-title">
                        Organization
                    </div>
                    <select
                        value={selectedOrg}
                        onChange={e => {
                            const org = e.target.value;
                            setSelectedOrg(org);
                            if (org === "All") {
                                return;
                            }
                            const item = list.find(
                                x => String(x.orgId) === org
                            );
                            if (item) {
                                setSelectedUsage(item.usage);
                            }
                        }}
                        className="inline-select"
                    >

                        <option value="All">
                            All
                        </option>
                        {
                            orgList.map(org => (
                                <option
                                    key={org}
                                    value={org}
                                >
                                    {org}
                                </option>
                            ))
                        }
                    </select>
                </div>
                <div className="card">
                    <div className="card-title">
                        Last Refresh
                    </div>
                    <div className="refresh-time">
                        {refreshTime || "-"}
                    </div>
                </div>
            </div>

            <div className="tabs">
                <button
                    className={`tab ${activeTab === "summary" ? "active" : ""}`}
                    onClick={() => setActiveTab("summary")}
                >
                    📊 Detail
                </button>
                <button
                    className={`tab ${activeTab === "trend" ? "active" : ""}`}
                    onClick={() => setActiveTab("trend")}
                >
                    📈 Trends
                </button>
            </div>
            {
                activeTab === "summary" && (

                    <SummaryView
                        timeZone={timeZone}
                        list={filteredList}
                        selectedOrg={selectedOrg}
                        loading={loading}
                        error={error}
                    />

                )
            }
            {
                activeTab === "trend" && (
                    <TrendView
                        list={filteredList}
                        selectedOrg={selectedOrg}
                        loading={loading}
                        error={error}
                    />
                )
            }
        </div>
    );
};

function formatDate(v) {
    if (!v) return "";
    return new Intl.DateTimeFormat(
        "en-US",
        {
            year:"numeric",
            month:"2-digit",
            day:"2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: false             
        }
    ).format(new Date(v));
}

export default CaseReport;